import {
  PATTERN_SIZE_MULTIPLIERS,
  Pattern,
  PatternMapping,
  PatternSizeLabel,
  StrokeOptions,
} from '@ritabygglov/core';

// eslint-disable-next-line react-refresh/only-export-components
export const SOLID_FILL_COLORS: Partial<Record<Pattern, string>> = {
  [PatternMapping.WHITE]: '#fff',
  [PatternMapping.DARK_GREY]: '#BABBBA',
  [PatternMapping.LIGHT_GREY]: '#D7D8D7',
  [PatternMapping.BLACK]: StrokeOptions.black,
  [PatternMapping.RED]: StrokeOptions.red,
  [PatternMapping.ORANGE]: StrokeOptions.orange,
  [PatternMapping.YELLOW]: StrokeOptions.yellow,
  [PatternMapping.GREEN]: StrokeOptions.green,
  [PatternMapping.BLUE]: StrokeOptions.blue,
};

const PATTERN_SIZE_LABELS = Object.keys(
  PATTERN_SIZE_MULTIPLIERS
) as PatternSizeLabel[];

// eslint-disable-next-line react-refresh/only-export-components
export const patternSizeToLabel = (patternSize?: number): PatternSizeLabel =>
  PATTERN_SIZE_LABELS.find(
    (label) => PATTERN_SIZE_MULTIPLIERS[label] === patternSize
  ) ?? 'M';

// eslint-disable-next-line react-refresh/only-export-components
export const getFillPattern = (
  pattern?: Pattern,
  diagonalHatch?: boolean,
  patternSize?: number
) => {
  if (pattern) {
    if (pattern === PatternMapping.NONE) return undefined;
    const solid = SOLID_FILL_COLORS[pattern];
    if (solid) return solid;
    return `url(#${pattern}-${patternSizeToLabel(patternSize)})`;
  }

  return diagonalHatch
    ? `url(#${PatternMapping['DIAGONAL_HATCH']}-M)`
    : undefined;
};

const SizedPattern = ({
  baseId,
  width,
  height,
  patternProps,
  children,
}: {
  baseId: string;
  width: number;
  height: number;
  patternProps?: Record<string, unknown>;
  children: React.ReactNode;
}) => (
  <>
    {PATTERN_SIZE_LABELS.map((label) => (
      <defs key={label}>
        <pattern
          id={`${baseId}-${label}`}
          patternUnits="userSpaceOnUse"
          width={width}
          height={height}
          patternTransform={`scale(${PATTERN_SIZE_MULTIPLIERS[label]})`}
          {...patternProps}
        >
          {children}
        </pattern>
      </defs>
    ))}
  </>
);

export const Patterns = () => (
  <>
    <SizedPattern
      baseId={PatternMapping['DIAGONAL_HATCH']}
      width={10}
      height={10}
    >
      <path d="M0,10 L10,0" stroke="black" strokeWidth="0.5" />
    </SizedPattern>

    <SizedPattern baseId={PatternMapping['WOOD_PANEL']} width={10} height={10}>
      <rect width="10" height="10" fill="white" />
      <path d="M10,10 L10,0" stroke="black" strokeWidth="1" />
    </SizedPattern>

    <SizedPattern
      baseId={PatternMapping['WOOD_PANEL_HORIZONTAL']}
      width={10}
      height={10}
    >
      <rect width="10" height="10" fill="white" />
      <path d="M0,10 L10,10" stroke="black" strokeWidth="1" />
    </SizedPattern>

    <SizedPattern baseId={PatternMapping['BRICK_WALL']} width={40} height={20}>
      <rect width="40" height="20" fill="white" />
      <g>
        <rect
          x="0"
          y="0"
          width="20"
          height="10"
          stroke="black"
          fill="none"
          strokeWidth="0.5"
        />
        <rect
          x="20"
          y="0"
          width="20"
          height="10"
          stroke="black"
          fill="none"
          strokeWidth="0.5"
        />
        <rect
          x="10"
          y="10"
          width="20"
          height="10"
          stroke="black"
          fill="none"
          strokeWidth="0.5"
        />
        <rect
          x="-10"
          y="10"
          width="20"
          height="10"
          stroke="black"
          fill="none"
          strokeWidth="0.5"
        />
        <rect
          x="30"
          y="10"
          width="20"
          height="10"
          stroke="black"
          fill="none"
          strokeWidth="0.5"
        />
      </g>
    </SizedPattern>

    <defs>
      <g id="pattern0_2605_56_inner">
        <path
          d="M153.5 -90.249L147.643 -85.8047C147.382 -85.607 147.064 -85.5 146.736 -85.5H141.264C140.936 -85.5 140.618 -85.607 140.357 -85.8047L134.5 -90.249V-103.994L139.753 -100.008C140.188 -99.678 140.721 -99.5 141.266 -99.5H146.734C147.279 -99.5 147.812 -99.678 148.247 -100.008L153.5 -103.994V-90.249Z"
          stroke="black"
          strokeLinejoin="round"
        />
      </g>
    </defs>
    <SizedPattern
      baseId={PatternMapping['ROOF_TILES']}
      width={19}
      height={13.8}
      patternProps={{
        x: '0',
        y: '0',
        fill: '#fff',
        patternContentUnits: 'objectBoundingBox',
        viewBox: '153 -91.2 19 13.8',
      }}
    >
      <use xlinkHref="#pattern0_2605_56_inner" />
      <use xlinkHref="#pattern0_2605_56_inner" transform="translate(19 0)" />
      <use xlinkHref="#pattern0_2605_56_inner" transform="translate(0 13.8)" />
      <use xlinkHref="#pattern0_2605_56_inner" transform="translate(19 13.8)" />
    </SizedPattern>

    <SizedPattern
      baseId={PatternMapping['STAGGED_SQUARE_WALL']}
      width={20}
      height={20}
    >
      <rect width="20" height="20" fill="white" />
      <rect
        x="0"
        y="0"
        width="10"
        height="10"
        stroke="black"
        fill="none"
        strokeWidth="0.25"
      />
      <rect
        x="10"
        y="0"
        width="10"
        height="10"
        stroke="black"
        fill="none"
        strokeWidth="0.25"
      />
      <rect
        x="5"
        y="10"
        width="10"
        height="10"
        stroke="black"
        fill="none"
        strokeWidth="0.25"
      />
      <rect
        x="-5"
        y="10"
        width="10"
        height="10"
        stroke="black"
        fill="none"
        strokeWidth="0.25"
      />
      <rect
        x="15"
        y="10"
        width="10"
        height="10"
        stroke="black"
        fill="none"
        strokeWidth="0.25"
      />
    </SizedPattern>

    <SizedPattern
      baseId={PatternMapping['CIRCULAR_DOTS']}
      width={15}
      height={15}
    >
      <rect width="15" height="15" fill="white" />
      <circle cx="7.5" cy="7.5" r="2" fill="black" />
    </SizedPattern>
  </>
);
